// src/components/TrialWarningBanner.js
import React, { useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  Typography,
  LinearProgress,
  Divider
} from '@mui/material';
import {
  Warning as WarningIcon,
  Error as ErrorIcon,
  Close as CloseIcon,
  CreditCard as CreditCardIcon,
  Schedule as ScheduleIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import useTrialStatus from '../hooks/useTrialStatus';

const TrialWarningBanner = () => {
  const navigate = useNavigate();
  const trialStatus = useTrialStatus();
  const [dismissed, setDismissed] = useState(false);

  // Don't show banner if loading, not trialing, or user dismissed it
  if (trialStatus.loading || (!trialStatus.isTrialing && !trialStatus.isExpired) || dismissed) {
    return null;
  }

  // Calculate severity and styling based on trial status
  const getSeverityConfig = () => {
    if (trialStatus.isExpired || trialStatus.isRestricted) {
      return {
        severity: 'error',
        icon: <ErrorIcon />,
        color: '#d32f2f',
        bgColor: '#ffebee',
        title: 'Trial Expired - Action Required',
        urgent: true
      };
    }
    
    if (trialStatus.daysRemaining !== null && trialStatus.daysRemaining <= 3) {
      return {
        severity: 'warning',
        icon: <WarningIcon />,
        color: '#ed6c02',
        bgColor: '#fff3e0',
        title: `Trial expires in ${trialStatus.daysRemaining} day${trialStatus.daysRemaining === 1 ? '' : 's'}`,
        urgent: trialStatus.daysRemaining <= 1
      };
    }

    if (trialStatus.isTrialing) {
      return {
        severity: 'info',
        icon: <ScheduleIcon />,
        color: '#0288d1',
        bgColor: '#e3f2fd',
        title: 'Trial Active',
        urgent: false
      };
    }

    return null;
  };

  const config = getSeverityConfig();
  
  if (!config) return null;

  const handleUpgrade = () => {
    // Navigate to billing/upgrade page or open Stripe portal
    window.open('/pricing', '_blank');
  };

  const handleAddPayment = () => {
    // Navigate to billing settings where they can add payment method
    navigate('/settings?tab=billing');
  };

  const formatDate = (date) => {
    if (!date) return '';
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const getProgressValue = () => {
    if (!trialStatus.daysRemaining) return 0;
    const totalTrialDays = 14; // Assuming 14-day trial
    const progress = ((totalTrialDays - trialStatus.daysRemaining) / totalTrialDays) * 100;
    return Math.min(100, Math.max(0, progress));
  };

  return (
    <Collapse in={!dismissed}>
      <Alert
        severity={config.severity}
        icon={config.icon}
        sx={{
          mb: 0,
          borderRadius: 0,
          borderLeft: `4px solid ${config.color}`,
          backgroundColor: config.bgColor,
          '& .MuiAlert-message': {
            width: '100%'
          }
        }}
        action={
          !config.urgent && (
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={() => setDismissed(true)}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          )
        }
      >
        <Box sx={{ width: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <AlertTitle sx={{ mb: 0, fontWeight: 600 }}>
              {config.title}
            </AlertTitle>
            
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {trialStatus.isRestricted && (
                <Chip 
                  icon={<LockIcon />}
                  label="Account Restricted" 
                  size="small" 
                  color="error" 
                  variant="outlined"
                />
              )}
              
              {trialStatus.daysRemaining !== null && !trialStatus.isExpired && (
                <Chip 
                  label={`${trialStatus.daysRemaining} days left`}
                  size="small" 
                  color={trialStatus.daysRemaining <= 3 ? 'warning' : 'info'}
                />
              )}
            </Box>
          </Box>

          <Box sx={{ mb: 2 }}>
            {trialStatus.isExpired ? (
              <Typography variant="body2" sx={{ mb: 1 }}>
                Your trial has expired. Your account is now in read-only mode. 
                {trialStatus.trialEndDate && ` Trial ended on ${formatDate(trialStatus.trialEndDate)}.`}
                <br />
                <strong>Add a payment method to restore full functionality and continue using MedInsight Pro.</strong>
              </Typography>
            ) : trialStatus.daysRemaining !== null && trialStatus.daysRemaining <= 3 ? (
              <Typography variant="body2" sx={{ mb: 1 }}>
                Your trial is ending soon. 
                {trialStatus.trialEndDate && ` Trial expires on ${formatDate(trialStatus.trialEndDate)}.`}
                <br />
                Add a payment method now to avoid any interruption in service.
              </Typography>
            ) : (
              <Typography variant="body2" sx={{ mb: 1 }}>
                You're currently on a free trial of MedInsight Pro.
                {trialStatus.trialEndDate && ` Trial expires on ${formatDate(trialStatus.trialEndDate)}.`}
              </Typography>
            )}

            {/* Progress bar for active trials */}
            {trialStatus.isTrialing && trialStatus.daysRemaining !== null && !trialStatus.isExpired && (
              <Box sx={{ mt: 1 }}>
                <LinearProgress 
                  variant="determinate" 
                  value={getProgressValue()}
                  sx={{ 
                    height: 6, 
                    borderRadius: 3,
                    backgroundColor: 'rgba(0,0,0,0.1)',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: trialStatus.daysRemaining <= 3 ? config.color : '#4caf50'
                    }
                  }}
                />
              </Box>
            )}
          </Box>

          <Divider sx={{ my: 1, opacity: 0.3 }} />

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            {trialStatus.isExpired ? (
              <>
                <Button
                  variant="contained"
                  color="error"
                  size="small"
                  startIcon={<CreditCardIcon />}
                  onClick={handleAddPayment}
                  sx={{ fontWeight: 600 }}
                >
                  Add Payment Method
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={handleUpgrade}
                >
                  Choose Plan
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="contained"
                  color={config.severity === 'warning' ? 'warning' : 'primary'}
                  size="small"
                  startIcon={<CreditCardIcon />}
                  onClick={handleUpgrade}
                  sx={{ fontWeight: 600 }}
                >
                  Choose Your Plan
                </Button>
                <Button
                  variant="text"
                  size="small"
                  onClick={() => window.open('https://medinsight.pro/demo', '_blank')}
                  sx={{ color: config.color }}
                >
                  Learn More
                </Button>
              </>
            )}

            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
              {trialStatus.error && (
                <Typography variant="caption" color="text.secondary">
                  Status check: {trialStatus.error}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </Alert>
    </Collapse>
  );
};

export default TrialWarningBanner;